/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./App.scss";
import { LiveAPIProvider, useLiveAPIContext } from "./contexts/LiveAPIContext";
import SidePanel from "./components/side-panel/SidePanel";
import { Altair } from "./components/altair/Altair";
import ControlTray from "./components/control-tray/ControlTray";
import VoicebotDisplay from "./components/voicebot-ui/VoicebotDisplay";
import PlayPauseButton from "./components/voicebot-ui/PlayPauseButton";
import TextInput from "./components/voicebot-ui/TextInput";
import { FlagIcon } from "./components/voicebot-ui/FlagIcon";
import cn from "classnames";
import { LiveClientOptions } from "./types";
import { useVisitorPresence } from "./hooks/use-visitor-presence";

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const DEFAULT_IMAGE =
  (process.env.PUBLIC_URL || "") + "/voicebot-hero.jpg";
const DEFAULT_IMAGE_2 =
  (process.env.PUBLIC_URL || "") + "/voicebot-hero2.jpg";

function MainContent({
  videoRef,
  videoStream,
  setVideoStream,
}: {
  videoRef: React.RefObject<HTMLVideoElement>;
  videoStream: MediaStream | null;
  setVideoStream: (s: MediaStream | null) => void;
}) {
  useVisitorPresence();
  const {
    connected,
    connect,
    disconnect,
    markUserActivity,
    client,
    airportMapUrl,
    setAirportMapUrl,
    showBookingInput,
    setShowBookingInput,
    waitingForResponse,
    remainingSeconds,
    language,
    setLanguage,
  } = useLiveAPIContext();

  const headerSubtitle =
    language === "vi"
      ? "Trải nghiệm ưu việt công nghệ H-AI từ Infodation B.V."
      : language === "nl"
        ? "Ervaar de superioriteit van H-AI-technologie door Infodation B.V."
        : "Experience the superiority of H-AI technology by Infodation B.V.";
  const placeholderNormal =
    language === "vi" ? "Nhập tin nhắn của bạn" : language === "nl" ? "Typ uw bericht" : "Type your message";
  const placeholderBooking =
    language === "vi"
      ? "Nhập mã đặt chỗ hoặc số điện thoại..."
      : language === "nl"
        ? "Voer boekingscode of telefoonnummer in..."
        : "Enter booking code or phone number...";

  let statusMessage: string | null = null;
  if (waitingForResponse) statusMessage = "Agent is looking...";
  else if (connected && remainingSeconds > 0) statusMessage = `Time ${formatCountdown(remainingSeconds)}`;

  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingActiveRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (!waitingForResponse) {
      typingActiveRef.current = false;
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      return;
    }
    typingActiveRef.current = true;
    const playTick = () => {
      try {
        const ctx = audioContextRef.current ?? new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
        if (!audioContextRef.current) audioContextRef.current = ctx;
        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = "sine";
        const freq = 900 + Math.random() * 500;
        const duration = 0.02 + Math.random() * 0.02;
        osc.frequency.setValueAtTime(freq, now);
        osc.frequency.exponentialRampToValueAtTime(freq * 0.7, now + duration);
        gain.gain.setValueAtTime(0.06 + Math.random() * 0.04, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
        osc.start(now);
        osc.stop(now + duration);
      } catch {
        // ignore if audio not allowed
      }
    };
    const scheduleNext = () => {
      if (!typingActiveRef.current) return;
      playTick();
      const delay = 60 + Math.random() * 220;
      typingTimeoutRef.current = setTimeout(scheduleNext, delay);
    };
    scheduleNext();
    return () => {
      typingActiveRef.current = false;
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [waitingForResponse]);

  const handlePlayPause = () => {
    if (connected) disconnect();
    else connect();
  };

  const handleTextSubmit = (text: string) => {
    markUserActivity();
    client.send([{ text }]);
    setShowBookingInput(false);
  };

  return (
    <div className="app-visible-root min-h-screen min-h-[100dvh] flex flex-col bg-background overflow-hidden">
      <header className="flex items-center justify-center gap-3 py-4 sm:py-6 px-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white flex items-center justify-center overflow-hidden">
          <img
            src={`${typeof window !== "undefined" ? window.location.origin : ""}${process.env.PUBLIC_URL || ""}/favicon.ico`}
            alt=""
            className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
          />
        </div>
        <div className="text-center">
          <h1 className="font-display text-lg sm:text-xl lg:text-2xl font-semibold gradient-text">
            IntelliCall® VoiceBot
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {headerSubtitle}
          </p>
          <div className="flex items-center justify-center gap-2 mt-2">
            <button
              type="button"
              onClick={() => setLanguage("vi")}
              className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden border-2 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background shrink-0 ${
                language === "vi"
                  ? "border-primary ring-2 ring-primary/50 bg-primary/10"
                  : "border-muted hover:border-muted-foreground/50 bg-muted/30"
              }`}
              title="Tiếng Việt"
              aria-label="Tiếng Việt"
            >
              <FlagIcon lang="vi" className="w-full h-full object-contain" />
            </button>
            <button
              type="button"
              onClick={() => setLanguage("en")}
              className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden border-2 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background shrink-0 ${
                language === "en"
                  ? "border-primary ring-2 ring-primary/50 bg-primary/10"
                  : "border-muted hover:border-muted-foreground/50 bg-muted/30"
              }`}
              title="English"
              aria-label="English"
            >
              <FlagIcon lang="en" className="w-full h-full object-contain" />
            </button>
            <button
              type="button"
              onClick={() => setLanguage("nl")}
              className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden border-2 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background shrink-0 ${
                language === "nl"
                  ? "border-primary ring-2 ring-primary/50 bg-primary/10"
                  : "border-muted hover:border-muted-foreground/50 bg-muted/30"
              }`}
              title="Nederlands"
              aria-label="Nederlands"
            >
              <FlagIcon lang="nl" className="w-full h-full object-contain" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-4 pb-4 gap-4 sm:gap-6 landscape:py-2">
        <div className="w-full max-w-4xl animate-fade-in">
          <VoicebotDisplay
            url={airportMapUrl}
            defaultImage={DEFAULT_IMAGE}
            defaultImage2={DEFAULT_IMAGE_2}
            isPlaying={connected}
            onCloseIframe={() => setAirportMapUrl(null)}
            statusMessage={statusMessage}
          />
        </div>

        <div className="py-2 sm:py-4 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <PlayPauseButton isPlaying={connected} onClick={handlePlayPause} />
        </div>

        <div className="w-full max-w-2xl animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <TextInput
            onSubmit={handleTextSubmit}
            placeholder={showBookingInput ? placeholderBooking : placeholderNormal}
            showClose={showBookingInput}
            onClose={() => setShowBookingInput(false)}
          />
        </div>
      </main>

      <footer className="py-3 sm:py-4 text-center">
        <p className="text-xs text-muted-foreground">
          <Link to="/dashboard" className="text-primary hover:underline mr-2">Call Monitor</Link>
          <Link to="/client" className="text-primary hover:underline mr-2">Client page</Link>
          · Powered by{" "}
          <a
            href="https://www.infodation.nl"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Infodation B.V.
          </a>{" "}
          © 2026
        </p>
      </footer>

      <Altair />
      <video
        className={cn("stream", "hidden")}
        ref={videoRef}
        autoPlay
        playsInline
      />

      <div className="control-tray-hidden" aria-hidden="true">
        <ControlTray
          videoRef={videoRef}
          supportsVideo={true}
          onVideoStreamChange={setVideoStream}
          enableEditingSettings={false}
        />
      </div>
    </div>
  );
}

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

function App() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);

  if (typeof API_KEY !== "string" || !API_KEY.trim()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-8">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold mb-2">Thiếu cấu hình</h1>
          <p className="text-muted-foreground text-sm">
            Tạo file <code className="bg-muted px-1 rounded">.env</code> và thêm:{" "}
            <code className="bg-muted px-1 rounded">REACT_APP_GEMINI_API_KEY=your_api_key</code>
          </p>
        </div>
      </div>
    );
  }

  const apiOptions: LiveClientOptions = { apiKey: API_KEY };

  return (
    <div className="App">
      <LiveAPIProvider options={apiOptions}>
        <div className="side-panel-wrapper side-panel-wrapper--hidden" aria-hidden="true">
          <SidePanel />
        </div>
        <MainContent
          videoRef={videoRef}
          videoStream={videoStream}
          setVideoStream={setVideoStream}
        />
      </LiveAPIProvider>
    </div>
  );
}

export default App;
